import { useEffect, useCallback, useRef } from 'react';

/**
 * Keyboard Shortcuts Hook
 * Provides keyboard shortcut functionality for components
 */

/**
 * useKeyboardShortcut
 *
 * @param {string|string[]} keys - Key combination (e.g., 'ctrl+s', ['ctrl+k', 'cmd+k'])
 * @param {function} callback - Function to call when shortcut is triggered
 * @param {object} options - Configuration options
 */
export const useKeyboardShortcut = (keys, callback, options = {}) => {
  const {
    enabled = true,
    preventDefault = true,
    enableOnFormTags = false,
    target = window,
  } = options;

  const callbackRef = useRef(callback);

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Normalize keys to array
  const keyArray = Array.isArray(keys) ? keys : [keys];

  const handleKeyDown = useCallback(
    (event) => {
      if (!enabled) return;

      // Skip if typing in form elements (unless explicitly enabled)
      if (
        !enableOnFormTags &&
        (event.target.tagName === 'INPUT' ||
          event.target.tagName === 'TEXTAREA' ||
          event.target.tagName === 'SELECT' ||
          event.target.isContentEditable)
      ) {
        return;
      }

      // Check if any of the key combinations match
      const matched = keyArray.some((keyCombo) => {
        const parts = keyCombo.toLowerCase().split('+');
        const key = parts[parts.length - 1];
        const modifiers = parts.slice(0, -1);

        // Check key
        const keyMatch =
          event.key.toLowerCase() === key ||
          event.code.toLowerCase() === key.toLowerCase() ||
          event.keyCode === parseInt(key);

        if (!keyMatch) return false;

        // Check modifiers
        const ctrlMatch = modifiers.includes('ctrl') ? event.ctrlKey : !event.ctrlKey;
        const altMatch = modifiers.includes('alt') ? event.altKey : !event.altKey;
        const shiftMatch = modifiers.includes('shift') ? event.shiftKey : !event.shiftKey;
        const metaMatch =
          modifiers.includes('meta') || modifiers.includes('cmd')
            ? event.metaKey
            : !event.metaKey;

        return ctrlMatch && altMatch && shiftMatch && metaMatch;
      });

      if (matched) {
        if (preventDefault) {
          event.preventDefault();
        }
        callbackRef.current(event);
      }
    },
    [keyArray, enabled, preventDefault, enableOnFormTags]
  );

  useEffect(() => {
    const targetElement = target === window ? window : target.current || target;

    if (targetElement) {
      targetElement.addEventListener('keydown', handleKeyDown);
      return () => {
        targetElement.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleKeyDown, target]);
};

/**
 * useKeyboardShortcuts
 * Register multiple shortcuts at once
 *
 * @param {object} shortcuts - Object mapping key combinations to callbacks
 * @param {object} options - Configuration options
 */
export const useKeyboardShortcuts = (shortcuts, options = {}) => {
  const {
    enabled = true,
    preventDefault = true,
    enableOnFormTags = false,
    target = window,
  } = options;

  const callbacksRef = useRef(shortcuts);

  // Update callbacks ref when shortcuts change
  useEffect(() => {
    callbacksRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!enabled) return;

      // Skip if typing in form elements (unless explicitly enabled)
      if (
        !enableOnFormTags &&
        (event.target.tagName === 'INPUT' ||
          event.target.tagName === 'TEXTAREA' ||
          event.target.tagName === 'SELECT' ||
          event.target.isContentEditable)
      ) {
        return;
      }

      // Check each registered shortcut
      for (const [keyCombo, callback] of Object.entries(callbacksRef.current)) {
        const keyArray = Array.isArray(keyCombo) ? keyCombo : [keyCombo];

        // Check if any of the key combinations match
        const matched = keyArray.some((combo) => {
          const parts = combo.toLowerCase().split('+');
          const key = parts[parts.length - 1];
          const modifiers = parts.slice(0, -1);

          // Check key
          const keyMatch =
            event.key.toLowerCase() === key ||
            event.code.toLowerCase() === key.toLowerCase() ||
            event.keyCode === parseInt(key);

          if (!keyMatch) return false;

          // Check modifiers
          const ctrlMatch = modifiers.includes('ctrl') ? event.ctrlKey : !event.ctrlKey;
          const altMatch = modifiers.includes('alt') ? event.altKey : !event.altKey;
          const shiftMatch = modifiers.includes('shift') ? event.shiftKey : !event.shiftKey;
          const metaMatch =
            modifiers.includes('meta') || modifiers.includes('cmd')
              ? event.metaKey
              : !event.metaKey;

          return ctrlMatch && altMatch && shiftMatch && metaMatch;
        });

        if (matched) {
          if (preventDefault) {
            event.preventDefault();
          }
          callback(event);
          break; // Only trigger first matching shortcut
        }
      }
    },
    [enabled, preventDefault, enableOnFormTags]
  );

  useEffect(() => {
    const targetElement = target === window ? window : target.current || target;

    if (targetElement) {
      targetElement.addEventListener('keydown', handleKeyDown);
      return () => {
        targetElement.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleKeyDown, target]);
};

/**
 * Global keyboard shortcuts map
 */
export const GLOBAL_SHORTCUTS = {
  // Navigation
  HOME: 'alt+h',
  DASHBOARD: 'alt+d',
  SESSIONS: 'alt+s',
  SEARCH: ['ctrl+k', 'cmd+k'],
  HELP: ['ctrl+/', 'cmd+/', '?'],

  // Actions
  SAVE: ['ctrl+s', 'cmd+s'],
  NEW: ['ctrl+n', 'cmd+n'],
  EDIT: ['ctrl+e', 'cmd+e'],
  DELETE: ['delete', 'backspace'],
  REFRESH: ['ctrl+r', 'cmd+r'],
  UNDO: ['ctrl+z', 'cmd+z'],
  REDO: ['ctrl+shift+z', 'cmd+shift+z'],
  COPY: ['ctrl+c', 'cmd+c'],
  PASTE: ['ctrl+v', 'cmd+v'],

  // Modal/Dialog
  CLOSE_MODAL: ['escape', 'esc'],
  CONFIRM: ['enter', 'ctrl+enter', 'cmd+enter'],

  // View
  FULLSCREEN: 'f11',
  ZOOM_IN: ['ctrl+=', 'cmd+=', 'ctrl+plus', 'cmd+plus'],
  ZOOM_OUT: ['ctrl+-', 'cmd+-', 'ctrl+minus', 'cmd+minus'],
  ZOOM_RESET: ['ctrl+0', 'cmd+0'],

  // Session Control
  START_SESSION: ['ctrl+shift+s', 'cmd+shift+s'],
  END_SESSION: ['ctrl+shift+e', 'cmd+shift+e'],
  NEXT_ACTIVITY: ['ctrl+right', 'cmd+right'],
  PREV_ACTIVITY: ['ctrl+left', 'cmd+left'],

  // Accessibility
  SKIP_TO_CONTENT: 'ctrl+shift+c',
  TOGGLE_SIDEBAR: 'ctrl+b',
};

/**
 * Keyboard shortcut descriptor for display
 */
export const getShortcutLabel = (keys) => {
  const keyArray = Array.isArray(keys) ? keys : [keys];
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  // Get the appropriate shortcut for the platform
  const platformKey = keyArray.find((key) => {
    if (isMac) {
      return key.includes('cmd') || !key.includes('ctrl');
    } else {
      return key.includes('ctrl') || !key.includes('cmd');
    }
  }) || keyArray[0];

  // Format the shortcut for display
  return platformKey
    .split('+')
    .map((part) => {
      switch (part.toLowerCase()) {
        case 'ctrl':
          return isMac ? '⌃' : 'Ctrl';
        case 'cmd':
        case 'meta':
          return '⌘';
        case 'alt':
          return isMac ? '⌥' : 'Alt';
        case 'shift':
          return isMac ? '⇧' : 'Shift';
        case 'enter':
          return '↵';
        case 'escape':
        case 'esc':
          return 'Esc';
        case 'backspace':
          return '⌫';
        case 'delete':
          return 'Del';
        case 'tab':
          return 'Tab';
        case 'space':
          return 'Space';
        case 'up':
        case 'down':
        case 'left':
        case 'right':
          return part.charAt(0).toUpperCase() + part.slice(1);
        default:
          return part.toUpperCase();
      }
    })
    .join(isMac ? '' : '+');
};

export default useKeyboardShortcut;
